import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';

// In-memory cache for profile picture URLs (persists during session)
const profilePictureCache = new Map();
// Cache for "no picture found" status to avoid repeated lookups
const noPictureCache = new Set();
// Ongoing fetch promises to prevent duplicate requests
const ongoingFetches = new Map();

// Shared fetch function used by both the component and preloadDancerThumbnails
const fetchAndCacheDancerPicture = async (dancerName) => {
  if (profilePictureCache.has(dancerName)) return profilePictureCache.get(dancerName);
  if (noPictureCache.has(dancerName)) return null;
  if (ongoingFetches.has(dancerName)) return ongoingFetches.get(dancerName);

  const fetchPromise = (async () => {
    try {
      const profileRef = doc(db, 'dancerProfiles', dancerName);
      const profileSnap = await getDoc(profileRef);
      let profilePictureFound = false;
      let pictureUrl = null;

      if (profileSnap.exists()) {
        const profileData = profileSnap.data();
        if (profileData.profilePicture) {
          try {
            if (profileData.profilePicture.startsWith('https://')) {
              pictureUrl = profileData.profilePicture;
              profilePictureFound = true;
            } else {
              const pictureRef = ref(storage, profileData.profilePicture);
              pictureUrl = await getDownloadURL(pictureRef);
              profilePictureFound = true;
            }
          } catch (e) {
            console.log('Could not load profile picture from dancerProfiles:', e);
          }
        }
      }

      if (!profilePictureFound) {
        const dancerRef = doc(db, 'dancers', dancerName);
        const dancerSnap = await getDoc(dancerRef);
        if (dancerSnap.exists()) {
          const dancerData = dancerSnap.data();
          if (dancerData.profilePicture) {
            try {
              const pictureRef = ref(storage, dancerData.profilePicture);
              pictureUrl = await getDownloadURL(pictureRef);
              profilePictureFound = true;
            } catch (e) {
              console.log('Could not load fallback profile picture from dancers collection:', e);
            }
          }
        }
      }

      if (profilePictureFound && pictureUrl) {
        profilePictureCache.set(dancerName, pictureUrl);
      } else {
        noPictureCache.add(dancerName);
      }
      return pictureUrl;
    } finally {
      ongoingFetches.delete(dancerName);
    }
  })();

  ongoingFetches.set(dancerName, fetchPromise);
  return fetchPromise;
};

// Preload pictures for a list of dancer names so they appear instantly when rendered
export const preloadDancerThumbnails = (dancerNames) => {
  if (!Array.isArray(dancerNames)) return;
  dancerNames.forEach(name => {
    if (name && !profilePictureCache.has(name) && !noPictureCache.has(name)) {
      fetchAndCacheDancerPicture(name).catch(() => {});
    }
  });
};

const DancerThumbnail = ({ 
  dancerName, 
  size = 40,
  className = '',
  style = {},
  showPlaceholder = true 
}) => {
  // Initialize synchronously from module-level cache — avoids any loading flash for preloaded pictures
  const [profilePictureUrl, setProfilePictureUrl] = useState(() =>
    profilePictureCache.has(dancerName) ? profilePictureCache.get(dancerName) : null
  );
  const [loading, setLoading] = useState(() =>
    !!dancerName && !profilePictureCache.has(dancerName) && !noPictureCache.has(dancerName)
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!dancerName) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    
    const loadProfilePicture = async () => {
      // Already resolved from cache synchronously — nothing to do
      if (profilePictureCache.has(dancerName) || noPictureCache.has(dancerName)) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(false);
        const url = await fetchAndCacheDancerPicture(dancerName);
        if (isMounted) setProfilePictureUrl(url);
      } catch (error) {
        console.log('Error loading dancer profile picture:', error);
        if (isMounted) {
          setError(true);
          setProfilePictureUrl(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadProfilePicture();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [dancerName]);

  // Don't render anything if loading and no picture found
  if (loading) {
    return showPlaceholder ? (
      <div 
        className={`dancer-thumbnail-loading ${className}`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: '#f0f0f0',
          display: 'inline-block',
          verticalAlign: 'middle',
          animation: 'pulse 1.5s ease-in-out infinite',
          ...style
        }}
      />
    ) : null;
  }

  // Don't render anything if no picture and placeholder disabled
  if (!profilePictureUrl && !showPlaceholder) {
    return null;
  }

  // Show profile picture if available
  if (profilePictureUrl) {
    return (
      <img
        src={profilePictureUrl}
        alt={`${dancerName} profile`}
        className={`dancer-thumbnail ${className}`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          display: 'inline-block',
          verticalAlign: 'middle',
          border: '2px solid #e0e0e0',
          backgroundColor: '#f9f9f9',
          ...style
        }}
        onError={() => {
          setError(true);
          setProfilePictureUrl(null);
        }}
      />
    );
  }

  // Show placeholder if enabled and no picture available
  if (showPlaceholder) {
    return (
      <div 
        className={`dancer-thumbnail-placeholder ${className}`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: '#e8f4fd',
          border: '2px solid #b3d9f2',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
          fontSize: Math.max(size * 0.4, 12),
          fontWeight: 'bold',
          color: '#2980b9',
          ...style
        }}
        title={`${dancerName} - No profile picture`}
      >
        {dancerName.charAt(0).toUpperCase()}
      </div>
    );
  }

  return null;
};

export default DancerThumbnail;
